import * as Api from '@ViewModels';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { ISetStartDateRequest, ITeamMemberAvailabilities } from '../types';
import { WebServiceHelper } from '../viewmodels/webServiceHelper';

function useCreateMeetingContext() {
	const loc = useLocation();
	const [meeting, setMeeting] = React.useState<Api.ISchedulerResponse>(undefined);
	const [isLoading, setIsLoading] = React.useState<boolean>(true);
	const webServiceHelperRef = React.useRef(new WebServiceHelper());
	const allowRetryRef = React.useRef(true);
	const pendingSavesRef = React.useRef(new Set<Promise<void>>());
	const [hasInvalidGuestEmail, setHasInvalidGuestEmail] = React.useState(false);

	const meetingBaseUrl = `scheduler/${encodeURIComponent(meeting?.id)}`;
	const baseUrl = webServiceHelperRef.current.baseUrl;

	const { shortCode, teamMeetingId, internalMeetingId, contactId, originalMeetingId } = React.useMemo(() => {
		let code = '';
		let tMeetingId = '';
		let iMeetingId = '';
		let cId = '';
		let origMeetingId = '';

		const urlParts = loc?.pathname?.split('/');
		if (urlParts?.length > 2) {
			const route = urlParts[1]?.toLocaleLowerCase();
			if (route === 'team') {
				tMeetingId = urlParts[2];
			} else if (route === 'internal') {
				iMeetingId = urlParts[2];
			} else {
				code = urlParts[1];
			}
		}

		if (loc?.search) {
			const params = new URLSearchParams(loc.search);
			cId = params.get('contactId') || '';
			origMeetingId = params.get('originalMeetingId') || '';
		}

		return {
			contactId: cId,
			internalMeetingId: iMeetingId,
			originalMeetingId: origMeetingId,
			shortCode: code,
			teamMeetingId: tMeetingId,
		};
	}, [loc]);

	const mainParticipant = React.useMemo(() => {
		return meeting?.contacts?.find(c => !c.isOptional);
	}, [meeting?.contacts]);

	const guestParticipants = React.useMemo(() => {
		if (teamMeetingId) {
			return meeting?.contacts?.filter((_, i) => i !== 0);
		}
		return meeting?.contacts?.filter(c => c.isOptional);
	}, [meeting?.contacts, teamMeetingId]);

	const allowSameDay = React.useMemo(() => {
		if (meeting?.minimumStartDate) {
			const startDate = new Date(meeting.minimumStartDate);
			const today = new Date(Date.now());
			return startDate.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0);
		}
		return false;
	}, [meeting?.minimumStartDate]);

	const isReschedule = !!originalMeetingId;

	const callWebServiceAsync = React.useCallback((url: string, method: Api.HTTPMethod, body?: any) => {
		const sendAsync = async () => {
			try {
				const result = await webServiceHelperRef.current.fetch<Api.ISchedulerResponse>(url, method, body);
				if (result.success) {
					setMeeting(current => ({ ...current, ...result.value }));
				} else {
					throw Api.asApiError(result);
				}
			} catch (err) {
				throw Api.asApiError(err);
			}
		};
		const save = sendAsync();
		const pendingSaves = pendingSavesRef.current;
		const forget = () => pendingSaves.delete(save);
		pendingSaves.add(save);
		save.then(forget, forget);
		return save;
	}, []);

	const altRequestMeeting = React.useCallback(
		(userInfo: Api.ISchedulerContact) => {
			return callWebServiceAsync(`${meetingBaseUrl}/requestother`, 'POST', {
				alternativeTime: userInfo.alternativeTime,
				contact: userInfo,
			});
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const reset = React.useCallback(() => {
		setMeeting(null);
	}, [setMeeting]);

	const scheduleMeeting = React.useCallback(async () => {
		// Fields save on blur, so the click on Schedule can start a save; commit after in-flight saves land.
		await Promise.all(pendingSavesRef.current);
		return callWebServiceAsync(`${meetingBaseUrl}/commit`, 'POST', {});
	}, [callWebServiceAsync, meetingBaseUrl]);

	const updateTitle = React.useCallback(
		(title: string) => {
			return callWebServiceAsync(`${meetingBaseUrl}/title?title=${encodeURIComponent(title)}`, 'PUT');
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const updateNotes = React.useCallback(
		(notes: string) => {
			return callWebServiceAsync(`${meetingBaseUrl}/comments`, 'PUT', { Comments: notes });
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const getAvailabilities = React.useCallback(
		async (monthOffset = 0): Promise<Api.ISchedulerDailyIntervals[][]> => {
			if (meeting?.id) {
				try {
					const result = await webServiceHelperRef.current.fetch<Api.ISchedulerDailyIntervals[][]>(
						`${meetingBaseUrl}/availability?monthOffset=${encodeURIComponent(monthOffset)}`,
						'GET'
					);
					if (result.success) {
						// Merge functionally and touch only availabilities: this runs right after a selected-host
						// change, and spreading a captured meeting would write the pre-change hosts back.
						setMeeting(prev => ({ ...prev, availabilities: result.value }));
						return result.value;
					} else {
						throw Api.asApiError(result);
					}
				} catch (err) {
					throw Api.asApiError(err);
				}
			} else {
				throw Api.asApiError('Meeting Id is not available');
			}
		},
		[meeting?.id, meetingBaseUrl]
	);

	const getTeamAvailabilities = React.useCallback(
		async (offset: number, config: Api.IMeetingConfigPriority): Promise<ITeamMemberAvailabilities> => {
			try {
				const result = await webServiceHelperRef.current.fetch<Api.ISchedulerDailyIntervals[][]>(
					`${meetingBaseUrl}/availability/${encodeURIComponent(
						config.id
					)}?monthOffset=${encodeURIComponent(offset.toString())}`,
					'GET'
				);
				if (result.success) {
					return { availabilities: result.value, config, success: true };
				}
				return { availabilities: null, config, success: false };
			} catch {
				return { availabilities: null, config, success: false };
			}
		},
		[meetingBaseUrl]
	);

	const init = React.useCallback(
		async (timezone: string) => {
			if (!!shortCode || !!teamMeetingId || !!internalMeetingId) {
				const params = new URLSearchParams();
				params.append('timeZoneId', timezone);
				if (contactId) {
					params.append('contactId', contactId);
				}
				if (originalMeetingId) {
					params.append('originalMeetingId', originalMeetingId);
				}

				// Team and internal sessions are created before the page loads, so they resume by meeting id
				// instead of minting a new draft from a short code.
				const existingMeetingId = teamMeetingId || internalMeetingId;

				try {
					setIsLoading(true);
					const result = await webServiceHelperRef.current.fetch<Api.ISchedulerResponse>(
						`scheduler/${encodeURIComponent(existingMeetingId || shortCode)}?${params.toString()}`,
						existingMeetingId ? 'GET' : 'POST'
					);

					if (result.success) {
						setMeeting(result.value);
						allowRetryRef.current = true;
					} else {
						allowRetryRef.current = false;
						throw Api.asApiError(result);
					}
				} catch (err) {
					allowRetryRef.current = false;
					throw Api.asApiError(err);
				} finally {
					setIsLoading(false);
				}
			}
		},
		[shortCode, teamMeetingId, internalMeetingId, contactId, originalMeetingId]
	);

	const createNewGuestParticipant = React.useCallback(
		(emailAddress: string) => {
			return callWebServiceAsync(`${meetingBaseUrl}/contact`, 'POST', {
				emailAddress,
				isOptional: true,
			});
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const removeGuestParticipant = React.useCallback(
		(guest: Api.ISchedulerContact) => {
			return callWebServiceAsync(`${meetingBaseUrl}/contact/${encodeURIComponent(guest.id)}`, 'DELETE');
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const setDateTimeV1 = React.useCallback(
		(startDate: Date) => {
			return callWebServiceAsync(
				`${meetingBaseUrl}/startdate?startDate=${encodeURIComponent(startDate.toISOString())}`,
				'PUT'
			);
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const setDateTimeV2 = React.useCallback(
		(startDate: Date, configIds: string[]) => {
			const request: ISetStartDateRequest = {
				configIds,
				startDate: startDate.toISOString(),
			};
			return callWebServiceAsync(`${meetingBaseUrl}/startdate/v2`, 'PUT', request);
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const setDateTime = React.useCallback(
		(startDate: Date, configIds?: string[]) => {
			if (teamMeetingId) {
				return setDateTimeV2(startDate, configIds);
			}
			return setDateTimeV1(startDate);
		},
		[teamMeetingId, setDateTimeV1, setDateTimeV2]
	);

	const setMainParticipant = React.useCallback(
		(participant: Api.ISchedulerContact) => {
			return callWebServiceAsync(
				`${meetingBaseUrl}/contact/${encodeURIComponent(mainParticipant?.id)}`,
				'PUT',
				participant
			);
		},
		[callWebServiceAsync, meetingBaseUrl, mainParticipant?.id]
	);

	const updateLocationPhone = React.useCallback(
		(location: Api.IPhoneMeetingLocation) => {
			return callWebServiceAsync(`${meetingBaseUrl}/location/phone`, 'POST', location);
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const updateLocationInPerson = React.useCallback(
		(location: Api.IInPersonMeetingLocation) => {
			return callWebServiceAsync(`${meetingBaseUrl}/location/inPerson`, 'POST', location);
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	const updateLocationVirtual = React.useCallback(
		(location: Api.IVirtualMeetingLocation) => {
			return callWebServiceAsync(`${meetingBaseUrl}/location/virtual`, 'POST', location);
		},
		[callWebServiceAsync, meetingBaseUrl]
	);

	return {
		isLoading,
		hasInvalidGuestEmail,
		guestParticipants,
		mainParticipant,
		shortCode,
		teamMeetingId,
		internalMeetingId,
		allowSameDay,
		originalMeetingId,
		isReschedule,
		meeting,
		baseUrl,
		webServiceHelper: webServiceHelperRef.current,

		init,
		reset,
		scheduleMeeting,
		updateTitle,
		updateNotes,
		altRequestMeeting,
		getAvailabilities,
		getTeamAvailabilities,
		createNewGuestParticipant,
		removeGuestParticipant,
		setDateTime,
		setHasInvalidGuestEmail,
		setMainParticipant,
		updateLocationPhone,
		updateLocationInPerson,
		updateLocationVirtual,
	};
}

export type IMeetingContext = ReturnType<typeof useCreateMeetingContext>;

const MeetingContext = React.createContext<IMeetingContext>(null);

export function MeetingProvider({ children }: React.PropsWithChildren<any>) {
	const context = useCreateMeetingContext();
	return <MeetingContext.Provider value={context}>{children}</MeetingContext.Provider>;
}

export const useMeeting = () => {
	const context = React.useContext(MeetingContext);
	if (!context) {
		throw new Error('useMeeting can only be used within the <ScheduleMeetingApp /> component');
	}
	return context;
};
